import { Nullable } from "../../../../../../types/common";
import { FlyerTemplate } from "../flyer-template/flyer-template.interface";
import { Canvas } from "../canvas/canvas.interface";
import { Grid } from "./grid.interface";
import {
  createGridLines,
  getColumnCoordinates,
  getRowCoordinates,
  scaleGridLines,
} from "./grid-line/grid-line.functions";
import { GridLine } from "./grid-line/grid-line.interface";
import { XyCoordinates } from "../xy-coordinates.interface";
import { toXyCoordinates } from "../canvas/canvas.functions";
import { Range } from "../../../../../../types/range/range";
import { Rectangle } from "../rectangle.interface";

export function getCellWidth(cell: Rectangle): number {
  return cell.x2 - cell.x1;
}

export function getCellHeight(cell: Rectangle): number {
  return cell.y2 - cell.y1;
}

export function getColumnPosition(cell: Rectangle): number {
  return Math.round(cell.x1 / getCellWidth(cell));
}

export function getRowPosition(cell: Rectangle): number {
  return Math.round(cell.y1 / getCellHeight(cell));
}

export function createGridCellsFromTemplateAndCanvas(
  template: Nullable<FlyerTemplate>,
  canvas: Nullable<Canvas>
): Rectangle[] {
  if (!canvas || !template) {
    return [];
  }

  let result: Rectangle[] = [];

  for (let x = 0; x < template.columns; x++) {
    for (let y = 0; y < template.rows; y++) {
      result = result.concat([createGridCell(x, y, template, canvas)]);
    }
  }

  return result;
}

export function createGridFromTemplate(
  template: Nullable<FlyerTemplate>,
  canvas: Nullable<Canvas>
): Nullable<Grid> {
  if (!template || !canvas) {
    return null;
  }

  return {
    rows: createGridLines(
      template.rows,
      { x: canvas.width, y: canvas.height },
      "row",
      getRowCoordinates(canvas.height, template.rows)
    ),
    columns: createGridLines(
      template.columns,
      { x: canvas.width, y: canvas.height },
      "col",
      getColumnCoordinates(canvas.width, template.columns)
    ),
    cells: createGridCellsFromTemplateAndCanvas(template, canvas),
  };
}

export function moveGridLine(
  line: GridLine,
  position: XyCoordinates,
  grid: Nullable<Grid>
): Nullable<Grid> {
  if (!grid) {
    return null;
  }

  if (line.type === "col") {
    const myColumns = [...grid.columns];
    myColumns.find((col) => col.index === line.index)!.x = position.x;
    return {
      ...grid,
      columns: myColumns,
    };
  }

  if (line.type === "row") {
    const myRows = [...grid.rows];
    myRows.find((row) => row.index === line.index)!.y = position.y;
    return {
      ...grid,
      rows: myRows,
    };
  }

  return null;
}

export function scaleToCanvas(
  template: FlyerTemplate,
  grid: Nullable<Grid>,
  newCanvas: Canvas,
  prevCanvas: Canvas
): Nullable<Grid> {
  if (!grid) {
    return null;
  }

  return {
    rows: scaleGridLines(grid.rows, toXyCoordinates(newCanvas), toXyCoordinates(prevCanvas)),
    columns: scaleGridLines(grid.columns, toXyCoordinates(newCanvas), toXyCoordinates(prevCanvas)),
    cells: createGridCellsFromTemplateAndCanvas(template, newCanvas),
  };
}

export function getColumnSpanWidth(range: Range, grid: Grid, canvas: Canvas): number {
  const minX = range.min === 0 ? 0 : grid.columns[range.min - 1].x;

  if (range.max === grid.columns.length) {
    return canvas.width - minX;
  }

  return grid.columns[range.max].x - minX;
}

export function getRowSpanHeight(range: Range, grid: Grid, canvas: Canvas): number {
  const minY = range.min === 0 ? 0 : grid.rows[range.min - 1].y;

  if (range.max === grid.rows.length) {
    return canvas.height - minY;
  }

  return grid.rows[range.max].y - minY;
}

function createGridCell(
  xIndex: number,
  yIndex: number,
  template: FlyerTemplate,
  canvas: Canvas
): Rectangle {
  const x1 = xIndex * getColumnWidth(template, canvas);
  const y1 = yIndex * getRowHeight(template, canvas);

  return {
    x1,
    y1,
    x2: x1 + getColumnWidth(template, canvas),
    y2: y1 + getRowHeight(template, canvas),
  };
}

function getColumnWidth(template: FlyerTemplate, canvas: Canvas): number {
  return canvas.width / template.columns;
}

function getRowHeight(template: FlyerTemplate, canvas: Canvas): number {
  return canvas.height / template.rows;
}
